import { useEffect, useRef, useState, type PointerEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { ArrowLeft, Check, ChevronDown, Circle, Lock, X } from "lucide-react";
import { ProgressRing } from "@/features/gettingStarted/ProgressRing";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { useViewer } from "@/hooks/useOrganizations";
import type { MyOrganization } from "@/lib/organizations";
import { cn } from "@/lib/utils";
import { OrgLogo } from "../OrgBits";
import { orgManageRoute } from "../orgRoutes";
import { localDay, nextOrgStep, orgStepHref, type OrgChecklistStep } from "./orgChecklist";
import { completedSeenFlag, hiddenFlag, readNudgeDay, setNudgeDay } from "./orgChecklistStorage";
import { useOrgChecklistFlag, useOrgChecklistRingTarget, useOrgGettingStarted } from "./useOrgGettingStarted";

const ORDER = { todo: 0, done: 1, locked: 2 } as const;
/** How long the daily "Next:" hint stays up before it gets out of the way. */
export const NUDGE_MS = 8000;
/** Grace period when the mouse leaves, so it can travel from the ring into the panel. */
export const HOVER_CLOSE_MS = 200;

/**
 * Top-bar ring for the org checklist (Spotify's "Getting started" ring): the
 * same list as the hub's Overview card, reachable from every page, each step
 * one tap from the section that teaches it. Wraps the avatar (`children`) in
 * one pill, and a mouse anywhere on the pill — avatar included — opens the
 * panel; touch and keyboard open it from the ring like any menu. Clicking the
 * avatar still opens the account menu, whose ring stays about being ready for
 * gigs (#832). Hide and progress are shared with the card through the same
 * per-org flags. With nothing to show, it renders the avatar alone.
 *
 * On a screen that can hover, the ring stays tucked away until the mouse (or
 * focus) reaches the avatar: at rest the corner is just the avatar. Touch
 * screens can't hover, so there the ring is always out (`.org-pill` in
 * index.css). The open panel and the daily nudge pin it out (`data-revealed`).
 *
 * The worker checklist on the avatar goes first (`suppressed`): org steps
 * only show up once the member is ready for gigs.
 *
 * On a phone the daily nudge is the ring's pulse alone: the "Next:" card
 * would hang over the top of whatever page is open (My Work's view tabs,
 * for one) until it timed out or was closed. The ring is always out there,
 * and one tap on it opens the same steps.
 */
export function OrgChecklistPill({
  children,
  suppressed = false,
}: {
  children: ReactNode;
  /** The worker's own getting-started steps are still up: they come first. */
  suppressed?: boolean;
}) {
  const target = useOrgChecklistRingTarget();
  if (suppressed || !target.org) return <>{children}</>;
  // Not keyed by org: a remount would close the panel the member just switched in.
  return (
    <RingFor org={target.org} others={target.candidates} onPick={target.pick}>
      {children}
    </RingFor>
  );
}

function RingFor({
  org,
  others,
  onPick,
  children,
}: {
  org: MyOrganization;
  others: readonly MyOrganization[];
  onPick: (orgId: string) => void;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const viewer = useViewer();
  const gs = useOrgGettingStarted(org);
  const markFlag = useOrgChecklistFlag(org.id);
  const [nudge, setNudge] = useState<OrgChecklistStep | null>(null);
  const [open, setOpen] = useState(false);
  // "orgs": the panel lists the member's orgs instead of the steps.
  const [view, setView] = useState<"steps" | "orgs">("steps");
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const phone = useIsMobile();
  useEffect(() => () => clearTimeout(closeTimer.current), []);

  const ready = gs.status === "ready";
  const next = ready ? nextOrgStep(gs.checklist) : null;

  // Once a calendar day: pulse the ring and point at the next step. Keyed on
  // the step's id — the checklist object is rebuilt on every render.
  const nextId = next?.id ?? null;
  useEffect(() => {
    if (!viewer || !next) return;
    const today = localDay();
    if (readNudgeDay(viewer) === today) return;
    setNudgeDay(viewer, today);
    setNudge(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on nextId (see above): next is rebuilt every render
  }, [viewer, nextId]);
  useEffect(() => {
    if (!nudge) return;
    const id = setTimeout(() => setNudge(null), NUDGE_MS);
    return () => clearTimeout(id);
  }, [nudge]);

  if (gs.status !== "ready") return <>{children}</>;
  const { checklist, tier } = gs;
  const changeOpen = (next: boolean) => {
    clearTimeout(closeTimer.current);
    setOpen(next);
    if (!next) setView("steps");
    if (next) setNudge(null);
    // Seeing "all set" in the panel is seeing it: the ring is done.
    else if (checklist.complete) markFlag(completedSeenFlag(tier));
  };
  // Hover is for a mouse only: a tap also fires pointerenter, and must toggle.
  const hoverIn = (e: PointerEvent) => {
    if (e.pointerType !== "mouse") return;
    clearTimeout(closeTimer.current);
    if (!open) changeOpen(true);
  };
  const hoverOut = (e: PointerEvent) => {
    if (e.pointerType !== "mouse") return;
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => changeOpen(false), HOVER_CLOSE_MS);
  };
  const go = (href: string) => {
    setNudge(null);
    navigate(href);
  };
  const steps = [...checklist.steps].sort((a, b) => ORDER[a.status] - ORDER[b.status]);
  const switchable = others.length > 1;
  const pick = (id: string) => {
    setNudge(null);
    setView("steps");
    onPick(id);
  };
  const ringLabel = t("orgs.hub.gettingStarted.ringLabel", { org: org.name, done: checklist.done, total: checklist.total });
  const label = (s: OrgChecklistStep) => t(`orgs.hub.gettingStarted.steps.${s.id}`, { org: org.name });

  return (
    <div
      data-testid="org-checklist-pill"
      onPointerEnter={hoverIn}
      onPointerLeave={hoverOut}
      data-revealed={open || nudge ? "" : undefined}
      className="org-pill relative flex items-center rounded-full bg-surface-tertiary py-0.5 pr-1.5"
    >
      {/* Not modal: a modal menu blocks pointer events outside it, and hover-to-close needs them. */}
      <DropdownMenu.Root open={open} onOpenChange={changeOpen} modal={false}>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            ref={triggerRef}
            data-testid="org-checklist-ring"
            // A click after hovering would toggle the open panel shut; for a
            // mouse, keep it open (Radix skips its handler when prevented).
            onPointerDown={(e) => {
              if (e.pointerType === "mouse" && open) e.preventDefault();
            }}
            className="org-pill-ring relative mr-0.5 flex h-11 w-11 items-center justify-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            aria-label={ringLabel}
          >
            {nudge && (
              <span aria-hidden className="absolute inset-1 rounded-full bg-brand-500/40 motion-safe:animate-ping" />
            )}
            <ProgressRing
              steps={checklist.steps.filter((s) => s.status !== "locked").map((s) => s.status === "done")}
              label={ringLabel}
            >
              {checklist.complete ? (
                <Check className="h-4 w-4 text-brand-500" aria-hidden />
              ) : (
                <span className="text-[10px] font-semibold tabular-nums text-text-secondary">
                  {checklist.done}/{checklist.total}
                </span>
              )}
            </ProgressRing>
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            data-testid="org-checklist-popover"
            onPointerEnter={hoverIn}
            onPointerLeave={hoverOut}
            // The ring sits outside the panel, so Radix would read a click on
            // it as "outside" and close the panel the hover just opened. (A
            // click on the avatar is outside on purpose: its own menu opens.)
            onPointerDownOutside={(e) => {
              if (triggerRef.current?.contains(e.target as Node)) e.preventDefault();
            }}
            className="z-50 w-[304px] max-w-[calc(100vw-16px)] rounded-2xl border border-border-default bg-surface p-2 shadow-2xl shadow-black/25"
            sideOffset={8}
            align="end"
            collisionPadding={8}
          >
            {view === "orgs" ? (
              <OrgList orgs={others} currentId={org.id} onPick={pick} onBack={() => setView("steps")} />
            ) : (
              <>
                <div className="flex items-start justify-between gap-2 px-2 pb-1 pt-1.5">
                  <p className="font-semibold text-text-primary">{t("orgs.hub.gettingStarted.ringTitle")}</p>
                  <p className="shrink-0 pt-0.5 text-sm font-medium tabular-nums text-text-muted">
                    {checklist.done}/{checklist.total}
                  </p>
                </div>
                {switchable ? (
                  <DropdownMenu.Item
                    data-testid="org-checklist-switcher"
                    // Stay open: the panel swaps to the org list.
                    onSelect={(e) => {
                      e.preventDefault();
                      setView("orgs");
                    }}
                    aria-label={t("orgs.hub.gettingStarted.switchOrg", { org: org.name })}
                    className="mb-1 flex min-h-11 cursor-pointer items-center gap-2 rounded-xl border border-border-default px-2 py-1.5 outline-none hover:bg-surface-tertiary focus:bg-surface-tertiary"
                  >
                    <OrgLogo url={org.logoUrl} name={org.name} size="sm" className="h-7 w-7 rounded-lg" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-text-primary">{org.name}</span>
                      <span className="block text-[11px] text-text-tertiary">
                        {t("orgs.hub.gettingStarted.orgCount", { count: others.length })}
                      </span>
                    </span>
                    <ChevronDown className="h-4 w-4 shrink-0 text-text-muted" aria-hidden />
                  </DropdownMenu.Item>
                ) : (
                  <p className="truncate px-2 pb-1 text-xs text-text-tertiary">{org.name}</p>
                )}
                {checklist.complete ? (
                  <p className="px-2 pb-2 text-sm text-text-secondary">{t("orgs.hub.gettingStarted.allSet")}</p>
                ) : (
                  <div role="group" aria-label={t("orgs.hub.gettingStarted.title", { org: org.name })}>
                    {steps.map((s) => (
                      <StepRow key={s.id} step={s} label={label(s)} onGo={() => go(orgStepHref(org, s))} />
                    ))}
                  </div>
                )}
              </>
            )}
            <DropdownMenu.Separator className="my-1 h-px bg-border-default" />
            <div className="flex items-center justify-between gap-2">
              <DropdownMenu.Item
                onSelect={() => go(orgManageRoute(org.slug))}
                className="flex min-h-11 cursor-pointer items-center rounded-lg px-2 text-sm text-text-secondary outline-none hover:bg-surface-tertiary focus:bg-surface-tertiary"
              >
                {t("orgs.hub.open")}
              </DropdownMenu.Item>
              {!checklist.complete && (
                <DropdownMenu.Item
                  data-testid="org-checklist-hide"
                  onSelect={() => markFlag(hiddenFlag(tier))}
                  className="flex min-h-11 cursor-pointer items-center rounded-lg px-2 text-sm text-text-tertiary outline-none hover:bg-surface-tertiary focus:bg-surface-tertiary"
                >
                  {t("orgs.hub.gettingStarted.hide")}
                </DropdownMenu.Item>
              )}
            </div>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
      {children}

      {nudge && !phone && (
        <div
          data-testid="org-checklist-nudge"
          role="status"
          className="absolute right-0 top-full z-40 mt-2 flex w-64 max-w-[calc(100vw-24px)] items-center gap-1 rounded-xl border border-border-default bg-surface p-1 shadow-xl shadow-black/20"
        >
          <button
            type="button"
            onClick={() => go(orgStepHref(org, nudge))}
            className="min-h-11 min-w-0 flex-1 rounded-lg px-2 py-1.5 text-left hover:bg-surface-tertiary"
          >
            <span className="block truncate text-[11px] text-text-tertiary">
              {t("orgs.hub.gettingStarted.nextIn", { org: org.name })}
            </span>
            <span className="block text-sm font-medium text-text-primary">{label(nudge)}</span>
          </button>
          <button
            type="button"
            onClick={() => setNudge(null)}
            aria-label={t("orgs.hub.gettingStarted.dismiss")}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-text-muted hover:bg-surface-tertiary"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>
      )}
    </div>
  );
}

/** The member's orgs, the one on show checked; picking one swaps the steps underneath. */
function OrgList({
  orgs,
  currentId,
  onPick,
  onBack,
}: {
  orgs: readonly MyOrganization[];
  currentId: string;
  onPick: (orgId: string) => void;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const rowClass =
    "flex min-h-11 w-full cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm outline-none hover:bg-surface-tertiary focus:bg-surface-tertiary";
  return (
    <div data-testid="org-checklist-orglist">
      <DropdownMenu.Item
        onSelect={(e) => {
          e.preventDefault();
          onBack();
        }}
        className={cn(rowClass, "font-semibold text-text-primary")}
      >
        <ArrowLeft className="h-4 w-4 shrink-0 text-text-muted" aria-hidden />
        {t("orgs.hub.gettingStarted.yourOrgs")}
      </DropdownMenu.Item>
      <div role="group" aria-label={t("orgs.hub.gettingStarted.yourOrgs")} className="max-h-80 overflow-y-auto">
        {orgs.map((o) => (
          <OrgListRow key={o.id} org={o} current={o.id === currentId} onPick={() => onPick(o.id)} className={rowClass} />
        ))}
      </div>
    </div>
  );
}

/**
 * One org in the switcher, with its own progress. Mounted only while the list
 * is open, so the per-org probes run on demand (react-query caches them, and
 * the org on show reuses the ring's own).
 */
function OrgListRow({
  org,
  current,
  onPick,
  className,
}: {
  org: MyOrganization;
  current: boolean;
  onPick: () => void;
  className: string;
}) {
  const { t } = useTranslation();
  const gs = useOrgGettingStarted(org);
  const progress = gs.status === "ready" ? gs.checklist : null;
  return (
    <DropdownMenu.Item
      data-testid="org-checklist-org"
      data-current={current || undefined}
      aria-current={current || undefined}
      onSelect={(e) => {
        e.preventDefault();
        onPick();
      }}
      className={cn(className, current && "bg-surface-tertiary")}
    >
      <OrgLogo
        url={org.logoUrl}
        name={org.name}
        size="sm"
        className={cn("h-7 w-7 rounded-lg", current && "ring-2 ring-brand-500 ring-offset-1 ring-offset-surface")}
      />
      <span className={cn("min-w-0 flex-1 truncate", current ? "font-medium text-text-primary" : "text-text-secondary")}>
        {org.name}
      </span>
      {/* Fixed width: the count arriving doesn't shift the name. */}
      <span data-testid="org-checklist-org-progress" className="flex w-9 shrink-0 justify-end">
        {progress === null ? (
          <span aria-hidden className="h-2 w-6 rounded-full bg-surface-tertiary motion-safe:animate-pulse" />
        ) : progress.complete ? (
          <Check className="h-4 w-4 text-brand-500" aria-label={t("orgs.hub.gettingStarted.orgDone")} />
        ) : (
          <span
            className="text-xs font-medium tabular-nums text-text-muted"
            aria-label={t("orgs.hub.gettingStarted.orgProgress", { done: progress.done, total: progress.total })}
          >
            {progress.done}/{progress.total}
          </span>
        )}
      </span>
    </DropdownMenu.Item>
  );
}

function StepRow({ step, label, onGo }: { step: OrgChecklistStep; label: string; onGo: () => void }) {
  const { t } = useTranslation();
  const Icon = step.status === "done" ? Check : step.status === "locked" ? Lock : Circle;
  const content = (
    <>
      <Icon className={cn("h-4 w-4 shrink-0", step.status === "done" ? "text-brand-500" : "text-text-muted")} aria-hidden />
      <span className="min-w-0 flex-1">
        <span className={step.status === "done" ? "text-text-muted line-through" : "text-text-primary"}>{label}</span>
        {step.status === "locked" && (
          <span className="block text-[11px] text-text-muted">{t("orgs.hub.gettingStarted.pollLocked")}</span>
        )}
      </span>
    </>
  );
  const rowClass = "flex min-h-10 w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm";
  return step.status === "todo" ? (
    <DropdownMenu.Item
      data-testid="org-checklist-step"
      data-step={step.id}
      onSelect={onGo}
      className={cn(rowClass, "cursor-pointer outline-none hover:bg-surface-tertiary focus:bg-surface-tertiary")}
    >
      {content}
    </DropdownMenu.Item>
  ) : (
    <div data-testid="org-checklist-step" data-step={step.id} data-status={step.status} className={rowClass}>
      {content}
    </div>
  );
}
